// apps/web/src/features/crm/leads/list/EngagementListTable.tsx
//
// Presentation layer over `<DataTable<EngagementListRow>>` for the v4 lead list
// (#2247 FR-1, #2307). No `useQuery` here so tests drive it with rows directly
// (mirrors `LeadsTable`). Pagination is SERVER-driven (the projection is
// cursor-paginated), so `pageSize` is set past the loaded count to disable the
// DataTable's client pagination — the page owns the "load more" cursor walk.
// Sort is server-driven too and routed through DataTable's controlled-sort
// props (#2310): only `updatedAt` is sortable. The lead column stacks the lead
// title over "client · source"; the No-client bucket stays a filter chip, NOT a
// column (BR-3).
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EngagementListRow } from '@magimp/shared';
import { DataTable, Icon, TwoLineCell, type Column, type Sort } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/index.js';
import { EngagementStageBadge } from './EngagementStageBadge.js';
import { EngagementScore } from './EngagementScore.js';
import { EngagementTaskIndicator } from './EngagementTaskIndicator.js';
import { LeadAssignmentControl } from '../components/LeadAssignmentControl.js';

export interface EngagementListTableProps {
  rows: readonly EngagementListRow[];
  onSelect: (row: EngagementListRow) => void;
  emptyState?: ReactNode;
  sort: 'recent' | 'oldest';
  onSortChange: (next: 'recent' | 'oldest') => void;
  showAssignee: boolean;
  canAssign: boolean;
}

export function EngagementListTable({
  rows,
  onSelect,
  emptyState,
  sort,
  onSortChange,
  showAssignee,
  canAssign,
}: EngagementListTableProps) {
  const { t } = useTranslation(['leads', 'common']);
  const columns: Column<EngagementListRow>[] = [
    {
      key: 'leadCode',
      label: t('leads:fields.publicCode'),
      render: (r) => <CodeCell row={r} />,
    },
    {
      key: 'lead',
      label: t('leads:list.lead'),
      render: (r) => <LeadCell row={r} />,
    },
    {
      key: 'tasks',
      label: t('leads:list.tasksColumn'),
      align: 'center',
      headerRender: () => <Icon name="check-square" size={15} />,
      render: (r) => (
        <EngagementTaskIndicator
          stage={r.stage}
          phase={r.phase}
          hasOpenTask={r.hasOpenTask}
          openTaskDueAt={r.openTaskDueAt}
        />
      ),
    },
    ...(showAssignee
      ? [
          {
            key: 'assignee',
            label: t('leads:fields.manager'),
            render: (r: EngagementListRow) => r.assigneeName ?? t('leads:fields.unassigned'),
          },
        ]
      : []),
    {
      key: 'stage',
      label: t('leads:fields.stage'),
      render: (r) => <EngagementStageBadge phase={r.phase} stage={r.stage} size="sm" />,
    },
    ...(canAssign
      ? [
          {
            key: 'assignment',
            label: '',
            align: 'right' as const,
            render: (r: EngagementListRow) =>
              r.assigneeId === null && r.leadId ? (
                <LeadAssignmentControl leadId={r.leadId} />
              ) : null,
          },
        ]
      : []),
    {
      key: 'score',
      label: t('leads:list.score'),
      render: (r) => <EngagementScore score={r.score} size={12} unratedVariant="unqualified" />,
    },
    {
      key: 'updatedAt',
      label: t('leads:list.updated'),
      align: 'right',
      sortable: true,
      // WHY: a sortable RenderColumn must supply `sortValue` (type-enforced), but
      // in controlled mode the server owns row order so it is never read here.
      sortValue: (r) => r.updatedAt,
      render: (r) => <WhenCell row={r} />,
    },
    {
      // Decorative trailing chevron (reference fidelity). Empty label → DataTable
      // treats the cell as a click-swallowing action cell; row nav still works
      // via the rest of the row.
      key: 'chevron',
      label: '',
      align: 'right',
      render: () => <Icon name="chevron-right" size={15} style={{ color: 'var(--fg-4)' }} />,
    },
  ];
  // WHY (#2310): the projection is server-ordered, so map the page's
  // `recent|oldest` onto DataTable's single sortable column (desc↔recent,
  // asc↔oldest) and translate a mobile clear (`null`) back to `recent`.
  const dataSort: Sort =
    sort === 'recent' ? { key: 'updatedAt', dir: 'desc' } : { key: 'updatedAt', dir: 'asc' };
  return (
    <DataTable<EngagementListRow>
      columns={columns}
      rows={rows}
      onSelect={onSelect}
      emptyState={emptyState}
      getRowKey={(r) => r.id}
      sort={dataSort}
      onSortChange={(s) => onSortChange(!s ? 'recent' : s.dir === 'asc' ? 'oldest' : 'recent')}
      // WHY: server cursor pagination owns paging — never fragment the loaded
      // set into client pages.
      pageSize={Math.max(rows.length, 1)}
    />
  );
}

function LeadCell({ row }: { row: EngagementListRow }) {
  const { t } = useTranslation(['leads']);
  const primary = row.title ?? row.name ?? t('leads:list.noContact');
  const company = row.company ? (
    row.company
  ) : (
    <span style={{ color: 'var(--status-warn-fg)' }}>{t('leads:list.noClientLinked')}</span>
  );
  // Drop the whole source segment (with its separator) when source is absent —
  // never leave a dangling « · ».
  const source = typeof row.source === 'string' && row.source.length > 0 ? row.source : null;
  const secondary = (
    <>
      {company}
      {source !== null ? ` · ${source}` : ''}
    </>
  );
  return <TwoLineCell primary={primary} secondary={secondary} title={primary} />;
}

function CodeCell({ row }: { row: EngagementListRow }) {
  const { t } = useTranslation(['leads']);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <span style={{ fontWeight: 500 }}>{row.leadCode}</span>
      {row.dealCode && (
        <span style={{ fontSize: 11, color: 'var(--fg-2)' }}>
          {t('leads:card.dealCode')} {row.dealCode}
        </span>
      )}
    </span>
  );
}

function WhenCell({ row }: { row: EngagementListRow }) {
  const { t } = useTranslation(['leads']);
  if (row.resumeAt) {
    const overdue = new Date(row.resumeAt).getTime() < Date.now();
    return (
      <span
        data-testid="engagement-resume-at"
        data-overdue={overdue}
        style={{ color: overdue ? 'var(--status-error-fg)' : 'var(--fg-2)', fontSize: 12 }}
      >
        {overdue ? `${t('leads:list.overdue')} · ` : ''}
        {t('leads:list.resumeAt', { date: formatDateTime(row.resumeAt) })}
      </span>
    );
  }
  return (
    <span data-testid="engagement-updated-at" style={{ color: 'var(--fg-2)', fontSize: 12 }}>
      {formatDateTime(row.updatedAt)}
    </span>
  );
}
